import type { MapGeoJSONFeature, Map as MLMap } from 'maplibre-gl';
import { distance, projectToSegment } from '../nav/geo';
import type { LngLat } from '../shared/types';

/**
 * 表示中の地図タイルの道路から、「高速道路の上か」と「近くの一般道」を調べる。
 * OpenFreeMap / MapTiler（OpenMapTiles 形式: transportation 層の class）と
 * Protomaps（roads 層の kind）の両方に対応する
 */

type Props = Record<string, unknown>;

const isHighway = (p: Props) => p.class === 'motorway' || (p.class === 'trunk' && p.toll === 1) || p.kind === 'highway';
/** 目的地を置いてよい一般道（歩道・線路・駐車場の通路などは除く） */
const LOCAL = new Set(['trunk', 'primary', 'secondary', 'tertiary', 'minor', 'major_road', 'medium_road', 'minor_road']);
const isLocal = (p: Props) => !isHighway(p) && p.ramp !== 1 && LOCAL.has(String(p.class ?? p.kind));

function roadFeatures(map: MLMap, p: LngLat, radiusPx: number): MapGeoJSONFeature[] {
  const c = map.project(p);
  return map
    .queryRenderedFeatures([
      [c.x - radiusPx, c.y - radiusPx],
      [c.x + radiusPx, c.y + radiusPx],
    ])
    .filter((f) => (f.sourceLayer === 'transportation' || f.sourceLayer === 'roads') && /LineString$/.test(f.geometry.type));
}

/** 道路の線の上で p にいちばん近い点 */
function nearestOn(f: MapGeoJSONFeature, p: LngLat): { point: LngLat; dist: number } | null {
  const g = f.geometry;
  const lines = (g.type === 'LineString' ? [g.coordinates] : g.type === 'MultiLineString' ? g.coordinates : []) as LngLat[][];
  let best: { point: LngLat; dist: number } | null = null;
  for (const line of lines) {
    for (let i = 0; i < line.length - 1; i++) {
      const { point } = projectToSegment(p, line[i], line[i + 1]);
      const d = distance(p, point);
      if (!best || d < best.dist) best = { point, dist: d };
    }
  }
  return best;
}

function nearest(features: MapGeoJSONFeature[], p: LngLat) {
  let best: { f: MapGeoJSONFeature; point: LngLat; dist: number } | null = null;
  for (const f of features) {
    const n = nearestOn(f, p);
    if (n && (!best || n.dist < best.dist)) best = { f, ...n };
  }
  return best;
}

/** p のいちばん近くの道路が高速道路なら true（道路から 60m 以上離れていれば false） */
export function isOnHighway(map: MLMap, p: LngLat): boolean {
  const n = nearest(roadFeatures(map, p, 30), p);
  return !!n && n.dist <= 60 && isHighway(n.f.properties);
}

/** p の近く（maxMeters 以内）の一般道の上の点。側道などに目的地をずらすのに使う */
export function nearestLocalRoad(map: MLMap, p: LngLat, maxMeters = 800): LngLat | null {
  const n = nearest(roadFeatures(map, p, 200).filter((f) => isLocal(f.properties)), p);
  return n && n.dist <= maxMeters ? n.point : null;
}
